<template>
  <div class="trvm-container">
    <!-- 页头 -->
    <header class="trvm-header">
      <h1 class="trvm-title">旅途管理</h1>
      <span class="trvm-sub">数据源：旅途主页（proj.travel）</span>
      <span class="trvm-user" v-if="me.username">{{ me.username }} · {{ me.role }}</span>
      <div class="trvm-actions">
        <router-link to="/travel" class="trvm-link">返回主页</router-link>
        <el-button size="small" icon="el-icon-brush" @click="openOrphans">清理未使用文件</el-button>
        <el-button size="small" @click="logout">退出登录</el-button>
        <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate">新增行程</el-button>
      </div>
    </header>

    <!-- 字段筛选区 -->
    <div class="trvm-filters">
      <el-input v-model="filters.id" size="small" clearable placeholder="ID" class="f-id" @keyup.enter.native="search" />
      <el-select
        v-model="filters.title"
        size="small"
        clearable
        filterable
        placeholder="标题"
        class="f-w"
        @change="search"
        @clear="search"
      >
        <el-option v-for="t in titleOptions" :key="t" :label="t" :value="t" />
      </el-select>
      <el-input v-model="filters.subtitle" size="small" clearable placeholder="副标题" class="f-w" @keyup.enter.native="search" />
      <el-select
        v-model="filters.author"
        size="small"
        clearable
        filterable
        placeholder="作者"
        class="f-w"
        @change="search"
        @clear="search"
      >
        <el-option v-for="a in authorOptions" :key="a" :label="a" :value="a" />
      </el-select>
      <el-input v-model="filters.content" size="small" clearable placeholder="内容" class="f-w" @keyup.enter.native="search" />
      <el-select v-model="filters.order" size="small" class="f-s" @change="search">
        <el-option label="新→旧" value="desc"></el-option>
        <el-option label="旧→新" value="asc"></el-option>
      </el-select>
      <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
      <el-button size="small" @click="resetFilters">重置</el-button>
    </div>

    <el-alert v-if="error" type="error" :title="error" show-icon :closable="false" class="trvm-error" />
    <div v-if="loadingOptions" class="trvm-loading-options">筛选选项加载中…</div>

    <!-- 列表 -->
    <div class="trvm-table-wrap" v-loading="loading">
      <table class="trvm-table">
        <thead>
          <tr>
            <th class="c-title">标题</th>
            <th class="c-subtitle">副标题</th>
            <th class="c-author">作者</th>
            <th class="c-content">内容</th>
            <th class="c-media">附件</th>
            <th class="c-time">收录时间</th>
            <th class="c-time">录入时间</th>
            <th class="c-op">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in items" :key="(row.id || '') + idx">
            <td class="c-title" data-label="标题">{{ row.title }}</td>
            <td class="c-subtitle" data-label="副标题">{{ row.subtitle || '—' }}</td>
            <td class="c-author" data-label="作者">{{ row.author || '—' }}</td>
            <td class="c-content" data-label="内容" :title="row.content">{{ excerpt(row.content, 56) }}</td>
            <td class="c-media" data-label="附件">
              <span class="media-tag" v-if="row.track && row.track.points"><i class="el-icon-map-location"></i>{{ row.track.points.length }}</span>
              <span class="media-tag" v-if="(row.images || []).length"><i class="el-icon-picture-outline"></i>{{ row.images.length }}</span>
              <span class="media-tag" v-if="(row.videos || []).length"><i class="el-icon-video-camera"></i>{{ row.videos.length }}</span>
              <span v-if="!(row.track && row.track.points) && !(row.images || []).length && !(row.videos || []).length" class="media-none">—</span>
            </td>
            <td class="c-time" data-label="收录时间">{{ tsToDate(row.createtime) }}</td>
            <td class="c-time" data-label="录入时间">{{ tsToDate(row.recordtime) }}</td>
            <td class="c-op" data-label="操作">
              <el-button size="mini" @click="openEdit(row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="removeItem(row)">删除</el-button>
            </td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="8" class="trvm-empty">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页 -->
    <div class="trvm-pager">
      <el-pagination
        background
        :layout="pagerLayout"
        :pager-count="isMobile ? 5 : 7"
        :total="total"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[10, 20, 50]"
        @current-change="query"
        @size-change="onSizeChange"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="form.id ? '编辑行程' : '新增行程'"
      :visible.sync="dialogVisible"
      :width="dialogWidth"
      custom-class="mng-form-dialog"
      :close-on-click-modal="false"
      @close="onDialogClose"
    >
      <el-form :model="form" :label-position="isMobile ? 'top' : 'right'" label-width="90px" size="small">
        <el-form-item label="标题" required>
          <el-input v-model="form.title" placeholder="如：环青海湖骑行" />
        </el-form-item>
        <el-form-item label="副标题">
          <el-input v-model="form.subtitle" placeholder="副标题（可空）" />
        </el-form-item>
        <el-form-item label="作者">
          <el-input v-model="form.author" placeholder="作者（可空，缺省我）" />
        </el-form-item>
        <el-form-item label="行记" required>
          <el-input type="textarea" :rows="7" v-model="form.content" placeholder="旅途见闻，多行请换行" />
        </el-form-item>

        <!-- 轨迹 -->
        <el-form-item label="轨迹">
          <div class="trvm-block">
            <div v-if="form.track" class="track-info">
              <span class="track-name">{{ form.track.name || '轨迹' }}</span>
              <span class="track-meta">{{ (form.track.points || []).length }} 个坐标点 · {{ form.track.format || '' }}</span>
              <el-button size="mini" type="text" @click="removeTrack">移除</el-button>
            </div>
            <div class="upload-row">
              <el-upload
                :show-file-list="false"
                :http-request="handleTrackUpload"
                accept=".gpx,.kml,.tcx"
                :disabled="uploadStat.track.count > 0"
              >
                <el-button size="mini" :loading="uploadStat.track.count > 0" icon="el-icon-upload2">
                  {{ uploadStat.track.count > 0 ? ('上传解析中 ' + uploadStat.track.percent + '%') : (form.track ? '替换轨迹文件' : '上传轨迹文件') }}
                </el-button>
              </el-upload>
              <span class="upload-hint">支持 GPX / KML / TCX，上传后自动解析坐标点</span>
            </div>
          </div>
        </el-form-item>

        <!-- 图片 -->
        <el-form-item label="图片">
          <div class="trvm-block">
            <div class="media-grid">
              <div v-for="(img, i) in form.images" :key="'img' + i" class="media-cell">
                <div class="media-thumb">
                  <img :src="mediaUrl(img.thumbUrl || img.url)" :alt="img.desc || img.name" />
                  <button type="button" class="media-del" @click="removeImage(i)"><i class="el-icon-close"></i></button>
                </div>
                <el-input
                  v-model="img.desc"
                  size="mini"
                  class="media-desc"
                  placeholder="描述（可空）"
                  maxlength="60"
                />
              </div>
              <!-- 上传占位：选中后立即出现，逐张显示进度 -->
              <div v-for="job in imageUploads" :key="'up' + job.id" class="media-cell is-uploading">
                <div class="media-thumb">
                  <img :src="job.previewUrl" :alt="job.name" />
                  <div class="media-upmask" :class="{ 'is-error': job.status === 'error' }">
                    <template v-if="job.status === 'error'">
                      <i class="el-icon-warning-outline"></i>
                      <span class="media-uppct">失败</span>
                    </template>
                    <template v-else>
                      <i class="el-icon-loading"></i>
                      <span class="media-uppct">{{ job.total > 0 ? Math.min(99, Math.floor(job.loaded / job.total * 100)) : 0 }}%</span>
                    </template>
                  </div>
                </div>
                <span class="media-upname" :title="job.name">{{ job.name }}</span>
              </div>
              <el-upload
                class="media-add"
                :show-file-list="false"
                :http-request="handleImageUpload"
                accept="image/*"
                multiple
                :disabled="imageUploads.length > 0"
              >
                <div class="media-add-box" :class="{ 'is-loading': imageUploads.length > 0 }">
                  <template v-if="imageUploads.length > 0">
                    <i class="el-icon-loading"></i>
                    <span class="media-add-pct">剩 {{ imageUploads.length }} 张</span>
                  </template>
                  <i v-else class="el-icon-plus"></i>
                </div>
              </el-upload>
            </div>
            <span class="upload-hint">{{ imageUploads.length > 0 ? '正在逐张上传，请稍候…' : '支持多张图片，可逐张删除，并为每张填写描述' }}</span>
          </div>
        </el-form-item>

        <!-- 视频 -->
        <el-form-item label="视频">
          <div class="trvm-block">
            <div v-if="form.videos.length || videoUploads.length" class="video-list">
              <div v-for="(v, i) in form.videos" :key="'vid' + i" class="video-item">
                <video :src="mediaUrl(v.url)" controls preload="metadata" class="video-thumb"></video>
                <span class="video-name" :title="v.name">{{ v.name || '视频' }}</span>
                <button type="button" class="media-del" @click="removeVideo(i)"><i class="el-icon-close"></i></button>
              </div>
              <!-- 上传占位：选中后立即出现，逐条显示进度 -->
              <div v-for="job in videoUploads" :key="'vup' + job.id" class="video-item is-uploading">
                <div class="video-thumb video-upthumb" :class="{ 'is-error': job.status === 'error' }">
                  <i :class="job.status === 'error' ? 'el-icon-warning-outline' : 'el-icon-loading'"></i>
                  <span class="video-uppct">{{ job.status === 'error' ? '失败' : (job.total > 0 ? Math.min(99, Math.floor(job.loaded / job.total * 100)) : 0) + '%' }}</span>
                </div>
                <span class="video-name" :title="job.name">{{ job.name }}</span>
              </div>
            </div>
            <div class="upload-row">
              <el-upload
                :show-file-list="false"
                :http-request="handleVideoUpload"
                accept="video/*"
                multiple
                :disabled="videoUploads.length > 0"
              >
                <el-button size="mini" :loading="videoUploads.length > 0" icon="el-icon-upload2">
                  {{ videoUploads.length > 0 ? ('上传中，剩 ' + videoUploads.length + ' 个') : '上传视频' }}
                </el-button>
              </el-upload>
              <span class="upload-hint">{{ videoUploads.length > 0 ? '正在逐个上传，请稍候…' : '支持多个视频' }}</span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="收录时间">
          <el-date-picker
            v-model="form.createMs"
            type="datetime"
            value-format="timestamp"
            placeholder="收录时间"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="录入时间">
          <el-date-picker
            v-model="form.recordMs"
            type="datetime"
            value-format="timestamp"
            placeholder="录入时间"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="照片时间校正">
          <el-input-number
            v-model="form.photoOffsetSec"
            :step="1"
            :precision="0"
            controls-position="right"
            placeholder="秒，正=照片往后"
            style="width: 100%"
          />
          <span class="upload-hint">单位秒：正值=照片沿轨迹后移，负值=前移；空或 0 表示不校正</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="cancelDialog">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>

    <!-- 未使用文件清理弹窗 -->
    <el-dialog
      title="未使用的文件"
      :visible.sync="orphanVisible"
      :width="dialogWidth"
      custom-class="mng-form-dialog"
    >
      <div class="orphan-head">
        <span class="orphan-sum">共 <b>{{ orphanFiles.length }}</b> 个 · 合计 {{ formatSize(orphanSize) }}</span>
        <el-button size="mini" icon="el-icon-refresh" :loading="orphanLoading" @click="loadOrphans">刷新</el-button>
      </div>
      <p class="orphan-tip">这些文件已不被任何行程或笔记记录引用（多为取消上传、删图、删记录后残留），删除后不可恢复。</p>
      <div class="orphan-table-wrap" v-loading="orphanLoading">
        <table class="orphan-table" v-if="orphanFiles.length">
          <thead>
            <tr>
              <th class="oc-check">
                <el-checkbox
                  :value="allOrphanSelected"
                  :indeterminate="orphanIndeterminate"
                  aria-label="全选未使用文件"
                  @change="toggleAllOrphans"
                />
              </th>
              <th class="oc-thumb">预览</th>
              <th class="oc-name-h">文件名</th>
              <th class="oc-size">大小</th>
              <th class="oc-time">修改时间</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="f in orphanFiles"
              :key="f.name"
              :class="{ 'is-sel': orphanChecked[f.name] }"
              @click="toggleOrphan(f.name)"
            >
              <td class="oc-check" @click.stop>
                <el-checkbox v-model="orphanChecked[f.name]" />
              </td>
              <td class="oc-thumb" @click.stop>
                <button
                  v-if="orphanKind(f.name) === 'image'"
                  type="button"
                  class="orphan-thumb is-img"
                  :title="'预览 ' + f.name"
                  @click="previewOrphan(f)"
                >
                  <img :src="orphanThumbUrl(f)" alt="" loading="lazy" @error="onThumbError(f)" />
                </button>
                <button
                  v-else
                  type="button"
                  class="orphan-thumb is-icon"
                  :title="'预览 ' + f.name"
                  @click="previewOrphan(f)"
                >
                  <i :class="orphanKindIcon(f.name)"></i>
                </button>
              </td>
              <td class="oc-name" :title="f.name">
                <span class="oc-name-text">{{ f.name }}</span>
                <span class="oc-kind" :class="'k-' + orphanKind(f.name)">{{ orphanKindLabel(f.name) }}</span>
              </td>
              <td class="oc-size">{{ formatSize(f.size) }}</td>
              <td class="oc-time">{{ formatTime(f.modTime) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="orphan-empty">没有未使用的文件</div>
      </div>
      <template #footer>
        <el-button size="small" @click="orphanVisible = false">关闭</el-button>
        <el-button
          size="small"
          type="danger"
          plain
          :disabled="!orphanSelected.length"
          @click="removeOrphans(false)"
        >删除选中{{ orphanSelected.length ? '（' + orphanSelected.length + '）' : '' }}</el-button>
        <el-button
          size="small"
          type="danger"
          :disabled="!orphanFiles.length"
          @click="removeOrphans(true)"
        >全部删除</el-button>
      </template>
    </el-dialog>

    <!-- 未使用文件·预览弹窗 -->
    <el-dialog
      title="文件预览"
      :visible.sync="previewVisible"
      :width="dialogWidth"
      custom-class="mng-form-dialog orphan-preview-dialog"
      append-to-body
      @closed="onPreviewClosed"
    >
      <div class="op-wrap" v-if="previewFile">
        <div class="op-stage">
          <img
            v-if="previewKind === 'image'"
            :src="orphanFileUrl(previewFile.name)"
            :alt="previewFile.name"
            class="op-img"
          />
          <video
            v-else-if="previewKind === 'video'"
            :src="orphanFileUrl(previewFile.name)"
            class="op-video"
            controls
            autoplay
          ></video>
          <div v-else class="op-void">
            <i :class="orphanKindIcon(previewFile.name)"></i>
            <p>轨迹文件（{{ orphanExt(previewFile.name) }}）无法在浏览器中直接预览</p>
            <p class="op-void-tip">可用下方「新窗口打开」查看内容。</p>
          </div>
        </div>
        <div class="op-meta">
          <span class="op-name" :title="previewFile.name">{{ previewFile.name }}</span>
          <span class="op-info">
            {{ orphanKindLabel(previewFile.name) }} · {{ formatSize(previewFile.size) }} · {{ formatTime(previewFile.modTime) }}
          </span>
        </div>
      </div>
      <template #footer>
        <a
          class="op-open-link"
          :href="previewFile ? orphanFileUrl(previewFile.name) : ''"
          target="_blank"
          rel="noopener"
        >
          <el-button size="small" icon="el-icon-view">新窗口打开</el-button>
        </a>
        <el-button size="small" type="primary" @click="previewVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import { UseUrl } from "../../utils/config";
import { mediaUrl as buildMediaUrl } from "../../utils/travel";

// 登录/鉴权复用 shicigame；数据读写走 dataservice 的 proj.travel
const AUTH_BASE = UseUrl + "/shicigame";
const BASE = UseUrl + "/travel/mng";
const UPLOAD_URL = UseUrl + "/travel/mng/upload";
// 上传并发上限：小批量并行提速，又不会把服务端（压图/剥 EXIF）压满
const UPLOAD_CONCURRENCY = 2;

export default {
  name: "travelmng",
  data() {
    return {
      me: { username: "", role: "" },
      filters: { id: "", title: "", subtitle: "", author: "", content: "", order: "desc" },
      // 列表筛选项：进入页面时一次性预加载
      titleOptions: [],
      authorOptions: [],
      loadingOptions: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      saving: false,
      error: "",
      dialogVisible: false,
      windowWidth: (typeof window !== "undefined" ? window.innerWidth : 1280),
      // 进行中的上传任务：{ id, type, loaded, total }，用于显示进度与并发计数
      uploads: [],
      uploadSeq: 0,
      // 图片上传占位（选中后先全部显示，再限并发上传）：
      // { id, name, previewUrl, loaded, total, status: 'uploading' | 'error' }
      imageUploads: [],
      imgQueue: [],
      imgActive: 0,
      // 视频上传占位（同图片：先占位后限并发上传）：{ id, name, loaded, total, status }
      videoUploads: [],
      vidQueue: [],
      vidActive: 0,
      // 本次会话新上传、尚未随记录保存的文件名（取消或关闭弹窗时删除，避免留下孤儿文件）
      pendingUploads: [],
      // 未使用文件（孤儿文件）清理
      orphanVisible: false,
      orphanLoading: false,
      orphanFiles: [],
      orphanChecked: {},
      orphanSize: 0,
      // 未使用文件预览
      previewVisible: false,
      previewFile: null,
      form: { id: "", title: "", subtitle: "", author: "", content: "", track: null, images: [], videos: [], createMs: null, recordMs: null, photoOffsetSec: null }
    }
  },
  computed: {
    // 窄屏（手机）判定：用于切换弹窗宽度、表单标签位置与分页布局
    isMobile() {
      return this.windowWidth <= 768;
    },
    dialogWidth() {
      return this.isMobile ? "94%" : "780px";
    },
    pagerLayout() {
      return this.isMobile ? "total, prev, pager, next" : "total, prev, pager, next, sizes";
    },
    // 按类型汇总上传状态：进行中的数量 + 总体进度百分比
    uploadStat() {
      const acc = {
        track: { count: 0, loaded: 0, total: 0 },
        image: { count: 0, loaded: 0, total: 0 },
        video: { count: 0, loaded: 0, total: 0 }
      };
      this.uploads.forEach(u => {
        const s = acc[u.type];
        if (!s) return;
        s.count += 1;
        s.loaded += u.loaded || 0;
        s.total += u.total || 0;
      });
      const out = {};
      Object.keys(acc).forEach(k => {
        const s = acc[k];
        out[k] = {
          count: s.count,
          // 总长度不可知时退化为不确定进度（0 表示未知，界面显示旋转图标）
          percent: s.total > 0 ? Math.min(99, Math.floor((s.loaded / s.total) * 100)) : 0
        };
      });
      return out;
    },
    // 勾选中的未使用文件（供删除按钮与计数使用）
    orphanSelected() {
      var checked = this.orphanChecked;
      return this.orphanFiles.filter(function (f) { return !!checked[f.name]; });
    },
    allOrphanSelected() {
      return this.orphanFiles.length > 0 && this.orphanSelected.length === this.orphanFiles.length;
    },
    orphanIndeterminate() {
      return this.orphanSelected.length > 0 && this.orphanSelected.length < this.orphanFiles.length;
    },
    // 预览文件类型：image / video / track / other
    previewKind() {
      return this.previewFile ? this.orphanKind(this.previewFile.name) : "";
    }
  },
  methods: {
    token() {
      return String(localStorage.getItem("shicigame_token") || "");
    },
    // 登录校验（借用 shicigame）：无 token 或失效 → 跳登录页
    checkAuth() {
      if (!this.token()) {
        this.$router.replace({ path: "/shicilogin", query: { redirect: "/travelmng" } });
        return;
      }
      var that = this;
      Vue.axios.post(AUTH_BASE + "/auth/me").then((response) => {
        var r = response.data || {};
        if (!r.loggedIn) {
          that.$router.replace({ path: "/shicilogin", query: { redirect: "/travelmng" } });
          return;
        }
        that.me = r.user || { username: "", role: "" };
        if (that.me.role !== "admin") {
          that.error = "当前账号无管理权限（需要 admin）";
        }
        that.query();
      }).catch(() => {
        that.$router.replace({ path: "/shicilogin", query: { redirect: "/travelmng" } });
      });
    },
    logout() {
      localStorage.removeItem("shicigame_token");
      localStorage.removeItem("shicigame_user");
      this.$router.replace({ path: "/shicilogin" });
    },
    query() {
      this.loading = true;
      this.error = "";
      var that = this;
      Vue.axios.post(BASE + "/list", {
        page: this.page,
        pageSize: this.pageSize,
        order: this.filters.order,
        id: this.filters.id,
        title: this.filters.title,
        subtitle: this.filters.subtitle,
        author: this.filters.author,
        content: this.filters.content
      }).then((response) => {
        var r = response.data || {};
        that.items = r.list || [];
        that.total = r.total || 0;
        that.loading = false;
      }).catch((err) => {
        that.loading = false;
        that.error = "查询失败: " + (err && err.message ? err.message : "未知错误");
      });
    },
    search() {
      this.page = 1;
      this.query();
    },
    resetFilters() {
      this.filters = { id: "", title: "", subtitle: "", author: "", content: "", order: "desc" };
      this.search();
    },
    onSizeChange() {
      this.page = 1;
      this.query();
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    blankForm() {
      return { id: "", title: "", subtitle: "", author: "", content: "", track: null, images: [], videos: [], createMs: null, recordMs: null, photoOffsetSec: null };
    },
    openCreate() {
      this.form = this.blankForm();
      this.pendingUploads = [];
      this.clearImageUploads();
      this.clearVideoUploads();
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.pendingUploads = [];
      this.clearImageUploads();
      this.clearVideoUploads();
      this.form = Object.assign(this.blankForm(), {
        id: row.id || "",
        title: row.title || "",
        subtitle: row.subtitle || "",
        author: row.author || "",
        content: row.content || "",
        track: row.track || null,
        images: Array.isArray(row.images) ? row.images.map(function (x) { return Object.assign({}, x); }) : [],
        videos: Array.isArray(row.videos) ? row.videos.map(function (x) { return Object.assign({}, x); }) : [],
        createMs: row.createtime ? row.createtime * 1000 : null,
        recordMs: row.recordtime ? row.recordtime * 1000 : null,
        // 记录里即为秒；0 视为不校正
        photoOffsetSec: row.photoTimeOffset ? row.photoTimeOffset : null
      });
      this.dialogVisible = true;
    },
    save() {
      if (!this.form.title.trim() || !this.form.content.trim()) {
        this.$message.warning("标题和行记不能为空");
        return;
      }
      this.saving = true;
      var that = this;
      var body = {
        id: this.form.id,
        title: this.form.title,
        subtitle: this.form.subtitle,
        author: this.form.author,
        content: this.form.content,
        track: this.form.track,
        images: this.form.images,
        videos: this.form.videos,
        createtime: this.form.createMs ? Math.floor(this.form.createMs / 1000) : 0,
        recordtime: this.form.recordMs ? Math.floor(this.form.recordMs / 1000) : 0,
        // 表单秒 → 记录秒；空或 0 表示不校正
        photoTimeOffset: (typeof this.form.photoOffsetSec === "number" && this.form.photoOffsetSec)
          ? Math.round(this.form.photoOffsetSec)
          : 0
      };
      Vue.axios.post(BASE + "/save", body).then((response) => {
        var r = response.data || {};
        that.saving = false;
        if (r.success === false) {
          that.$message.error(r.message || "保存失败");
          return;
        }
        that.$message.success(r.message || "保存成功");
        // 文件已被记录引用，不再作为待回收文件
        that.pendingUploads = [];
        that.dialogVisible = false;
        that.query();
      }).catch((err) => {
        that.saving = false;
        that.$message.error("保存失败: " + (err && err.message ? err.message : ""));
      });
    },
    // 登记一个上传任务并入队，返回该任务条目（用于读取进度）
    beginUpload(file, type) {
      const entry = { id: ++this.uploadSeq, type: type, loaded: 0, total: (file && file.size) || 0 };
      this.uploads.push(entry);
      return entry;
    },
    endUpload(entry) {
      const idx = this.uploads.indexOf(entry);
      if (idx >= 0) this.uploads.splice(idx, 1);
    },
    // 上传单个文件到旅途附件接口（带进度回调），成功返回 { url, name, size, mime, track? }
    uploadFile(file, entry) {
      var that = this;
      var fd = new FormData();
      fd.append("file", file);
      return axios.post(UPLOAD_URL, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: function (e) {
          if (!e) return;
          if (typeof e.loaded === "number") entry.loaded = e.loaded;
          // lengthComputable 为真时才有 total
          if (e.total) entry.total = e.total;
        }
      })
        .then(function (response) {
          var r = (response && response.data) || {};
          if (r.success === false) {
            throw new Error(r.message || "上传失败");
          }
          return r;
        })
        .then(function (r) {
          that.endUpload(entry);
          return r;
        }, function (err) {
          that.endUpload(entry);
          throw err;
        });
    },
    // 删除本次新上传但未保存的文件；失败只记录，不打扰用户
    deleteFiles(names) {
      var list = (names || []).filter(function (x) { return !!x; });
      if (!list.length) return Promise.resolve();
      return axios.post(BASE + "/file/delete", { files: list }).catch(function (err) {
        if (window.console) window.console.warn("清理未保存的上传文件失败", err);
      });
    },
    // 登记本次新上传的文件；若弹窗已关闭（上传过程中点了取消）则立即删除，避免留下孤儿文件
    keepUploaded(names) {
      if (!this.dialogVisible) {
        this.deleteFiles(names);
        return false;
      }
      this.pendingUploads = this.pendingUploads.concat(names);
      return true;
    },
    // 取消/关闭编辑弹窗：把「已上传但未保存」的文件回收掉
    discardPending() {
      if (!this.pendingUploads.length) return;
      var names = this.pendingUploads;
      this.pendingUploads = [];
      this.deleteFiles(names);
    },
    cancelDialog() {
      this.dialogVisible = false;
      this.clearImageUploads();
      this.clearVideoUploads();
      this.discardPending();
    },
    onDialogClose() {
      // 点右上角 × / Esc / 点遮罩关闭时同样回收（保存成功时 pendingUploads 已清空，不会误删）
      this.clearImageUploads();
      this.clearVideoUploads();
      this.discardPending();
    },
    handleTrackUpload(opts) {
      var that = this;
      var entry = this.beginUpload(opts.file, "track");
      this.uploadFile(opts.file, entry).then(function (r) {
        if (!r.track) {
          that.deleteFiles([r.url]);
          that.$message.error("未从文件中解析到轨迹数据");
          return;
        }
        if (!that.keepUploaded([r.url])) return;
        that.form.track = r.track;
        that.$message.success("轨迹已解析：" + (r.track.points || []).length + " 个坐标点");
        opts.onSuccess && opts.onSuccess(r);
      }).catch(function (err) {
        that.$message.error("轨迹上传失败：" + (err && err.message ? err.message : ""));
        opts.onError && opts.onError(err);
      });
    },
    // 选中图片后先登记占位（一次选中多张会同时出现），再限并发上传
    handleImageUpload(opts) {
      var file = opts.file;
      var job = {
        id: ++this.uploadSeq,
        name: (file && file.name) || "图片",
        previewUrl: (typeof URL !== "undefined" && URL.createObjectURL) ? URL.createObjectURL(file) : "",
        // 复用 uploadFile 的进度字段
        loaded: 0,
        total: (file && file.size) || 0,
        status: "uploading"
      };
      this.imageUploads.push(job);
      this.imgQueue.push({ opts: opts, job: job });
      var that = this;
      // 延到本轮事件结束再启动，保证一次选中的全部文件都已登记
      this.$nextTick(function () { that.runImageQueue(); });
    },
    // 限并发泵：最多同时 UPLOAD_CONCURRENCY 张在传，其余排队
    runImageQueue() {
      var that = this;
      while (this.imgActive < UPLOAD_CONCURRENCY && this.imgQueue.length) {
        this.imgActive += 1;
        (function (item) {
          var job = item.job;
          that.uploadFile(item.opts.file, job).then(function (r) {
            that.keepUploaded([r.url, r.thumbUrl]);
            that.form.images.push({
              url: r.url,
              name: r.name,
              size: r.size,
              mime: r.mime,
              desc: "",
              thumbUrl: r.thumbUrl,
              time: r.time,
              timeTrusted: r.timeTrusted
            });
            that.finishImageUpload(job);
            item.opts.onSuccess && item.opts.onSuccess(r);
          }).catch(function (err) {
            that.$message.error("图片上传失败：" + (err && err.message ? err.message : ""));
            that.failImageUpload(job);
            item.opts.onError && item.opts.onError(err);
          }).then(function () {
            that.imgActive = Math.max(0, that.imgActive - 1);
            that.runImageQueue();
          });
        })(this.imgQueue.shift());
      }
    },
    // 上传成功：移除占位并释放预览地址
    finishImageUpload(job) {
      this.releaseImageJob(job);
    },
    // 上传失败：保留占位显示「失败」，稍后自动移除
    failImageUpload(job) {
      job.status = "error";
      var that = this;
      setTimeout(function () { that.releaseImageJob(job); }, 2500);
    },
    releaseImageJob(job) {
      var idx = this.imageUploads.indexOf(job);
      if (idx >= 0) this.imageUploads.splice(idx, 1);
      if (job.previewUrl) {
        try { URL.revokeObjectURL(job.previewUrl); } catch (e) { /* ignore */ }
        job.previewUrl = "";
      }
    },
    // 关闭/重置弹窗时清掉未完成的上传占位
    clearImageUploads() {
      this.imageUploads.forEach(function (job) {
        if (job.previewUrl) {
          try { URL.revokeObjectURL(job.previewUrl); } catch (e) { /* ignore */ }
        }
      });
      this.imageUploads = [];
      this.imgQueue = [];
      this.imgActive = 0;
    },
    // 选中视频后先登记占位（一次选中多个会同时出现），再限并发上传
    handleVideoUpload(opts) {
      var file = opts.file;
      var job = {
        id: ++this.uploadSeq,
        name: (file && file.name) || "视频",
        loaded: 0,
        total: (file && file.size) || 0,
        status: "uploading"
      };
      this.videoUploads.push(job);
      this.vidQueue.push({ opts: opts, job: job });
      var that = this;
      this.$nextTick(function () { that.runVideoQueue(); });
    },
    // 限并发泵：最多同时 UPLOAD_CONCURRENCY 个在传，其余排队
    runVideoQueue() {
      var that = this;
      while (this.vidActive < UPLOAD_CONCURRENCY && this.vidQueue.length) {
        this.vidActive += 1;
        (function (item) {
          var job = item.job;
          that.uploadFile(item.opts.file, job).then(function (r) {
            that.keepUploaded([r.url]);
            that.form.videos.push({ url: r.url, name: r.name, size: r.size, mime: r.mime });
            that.finishVideoUpload(job);
            item.opts.onSuccess && item.opts.onSuccess(r);
          }).catch(function (err) {
            that.$message.error("视频上传失败：" + (err && err.message ? err.message : ""));
            that.failVideoUpload(job);
            item.opts.onError && item.opts.onError(err);
          }).then(function () {
            that.vidActive = Math.max(0, that.vidActive - 1);
            that.runVideoQueue();
          });
        })(this.vidQueue.shift());
      }
    },
    finishVideoUpload(job) {
      this.releaseVideoJob(job);
    },
    failVideoUpload(job) {
      job.status = "error";
      var that = this;
      setTimeout(function () { that.releaseVideoJob(job); }, 2500);
    },
    releaseVideoJob(job) {
      var idx = this.videoUploads.indexOf(job);
      if (idx >= 0) this.videoUploads.splice(idx, 1);
    },
    // 关闭/重置弹窗时清掉未完成的视频上传占位
    clearVideoUploads() {
      this.videoUploads = [];
      this.vidQueue = [];
      this.vidActive = 0;
    },
    removeTrack() {
      this.form.track = null;
    },
    removeImage(idx) {
      this.form.images.splice(idx, 1);
    },
    removeVideo(idx) {
      this.form.videos.splice(idx, 1);
    },
    // 附件 URL → 可访问地址（补上 /api 前缀）
    mediaUrl(url) {
      return buildMediaUrl(url, UseUrl);
    },
    formatSize(n) {
      var v = Number(n) || 0;
      if (v < 1024) return v + " B";
      if (v < 1048576) return (v / 1024).toFixed(1) + " KB";
      if (v < 1073741824) return (v / 1048576).toFixed(2) + " MB";
      return (v / 1073741824).toFixed(2) + " GB";
    },
    formatTime(ts) {
      if (!ts) return "-";
      var d = new Date(ts * 1000);
      var p = function (x) { return x < 10 ? "0" + x : "" + x; };
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + " " + p(d.getHours()) + ":" + p(d.getMinutes());
    },
    openOrphans() {
      this.orphanVisible = true;
      this.loadOrphans();
    },
    loadOrphans() {
      var that = this;
      this.orphanLoading = true;
      return axios.post(BASE + "/orphan/list", {}).then(function (r) {
        var d = (r && r.data) || {};
        that.orphanLoading = false;
        var files = Array.isArray(d.files) ? d.files : [];
        // 预先建好每个文件名的勾选键，保证 v-model 可响应
        var checked = {};
        files.forEach(function (f) { checked[f.name] = false; });
        that.orphanChecked = checked;
        that.orphanFiles = files;
        that.orphanSize = d.size || 0;
        if (d.message) that.$message.error(d.message);
      }).catch(function (err) {
        that.orphanLoading = false;
        that.$message.error("读取未使用文件失败：" + (err && err.message ? err.message : ""));
      });
    },
    // 表头全选/取消全选
    toggleAllOrphans(val) {
      var checked = {};
      var on = !!val;
      this.orphanFiles.forEach(function (f) { checked[f.name] = on; });
      this.orphanChecked = checked;
    },
    // 单行切换选中（文件名键已在 loadOrphans 中预建，直接赋值即可响应）
    toggleOrphan(name) {
      this.orphanChecked[name] = !this.orphanChecked[name];
    },
    // 由扩展名判断未使用文件的类型：image / video / track / other
    orphanKind(name) {
      var ext = this.orphanExt(name).toLowerCase();
      if (ext === "gpx" || ext === "kml" || ext === "tcx") return "track";
      if (["mp4", "mov", "m4v", "webm", "avi", "mkv"].indexOf(ext) >= 0) return "video";
      if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].indexOf(ext) >= 0) return "image";
      return "other";
    },
    orphanKindLabel(name) {
      var k = this.orphanKind(name);
      if (k === "track") return "轨迹";
      if (k === "video") return "视频";
      if (k === "image") return "图片";
      return "文件";
    },
    orphanKindIcon(name) {
      var k = this.orphanKind(name);
      if (k === "video") return "el-icon-video-camera";
      if (k === "track") return "el-icon-map-location";
      if (k === "image") return "el-icon-picture-outline";
      return "el-icon-document";
    },
    orphanExt(name) {
      var m = /\.([a-z0-9]+)$/i.exec(String(name || ""));
      return m ? m[1] : "";
    },
    // 存盘文件名 → 可访问地址（静态路由 /travel/file/:name，与笔记附件共用目录）
    orphanFileUrl(name) {
      if (!name) return "";
      return buildMediaUrl("/travel/file/" + encodeURIComponent(name), UseUrl);
    },
    // 图片中图名 → 小图名；已是小图或 gif 等无 _m 后缀的按其基准名拼 _t.jpg
    orphanThumbName(name) {
      var n = String(name || "");
      if (/_t\.[a-z0-9]+$/i.test(n)) return n;
      return n.replace(/\.[^.]+$/, "").replace(/_m$/, "") + "_t.jpg";
    },
    // 缩略图地址：优先小图，加载失败（小图不存在）时回退到文件本身
    orphanThumbUrl(f) {
      if (!f) return "";
      if (f.thumbFail) return this.orphanFileUrl(f.name);
      return this.orphanFileUrl(this.orphanThumbName(f.name));
    },
    onThumbError(f) {
      if (f && !f.thumbFail) this.$set(f, "thumbFail", true);
    },
    previewOrphan(f) {
      this.previewFile = f;
      this.previewVisible = true;
    },
    onPreviewClosed() {
      this.previewFile = null;
    },
    removeOrphans(all) {
      var that = this;
      var n = all ? this.orphanFiles.length : this.orphanSelected.length;
      if (!n) return;
      var tip = all
        ? "确定删除全部 " + n + " 个未使用文件？此操作不可恢复。"
        : "确定删除选中的 " + n + " 个文件？此操作不可恢复。";
      this.$confirm(tip, "删除确认", { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" })
        .then(function () {
          var body = all ? { all: true } : { files: that.orphanSelected.map(function (f) { return f.name; }) };
          return axios.post(BASE + "/orphan/delete", body);
        })
        .then(function (r) {
          var d = (r && r.data) || {};
          if (d.success === false) {
            that.$message.error(d.message || "删除失败");
          } else {
            that.$message.success(d.message || "已删除");
          }
          return that.loadOrphans();
        })
        .catch(function (err) {
          // 点「取消」时 reject 的是字符串，不是真错误
          if (err !== "cancel" && err !== "close") {
            that.$message.error("删除失败：" + (err && err.message ? err.message : ""));
          }
        });
    },
    removeItem(row) {
      var that = this;
      this.$confirm(`确定删除「${row.title}」？此操作不可恢复。`, "删除确认", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        Vue.axios.post(BASE + "/delete", { id: row.id }).then((response) => {
          var r = response.data || {};
          if (r.success === false) {
            that.$message.error(r.message || "删除失败");
            return;
          }
          that.$message.success("已删除");
          if (that.items.length === 1 && that.page > 1) {
            that.page -= 1;
          }
          that.query();
        }).catch((err) => {
          that.$message.error("删除失败: " + (err && err.message ? err.message : ""));
        });
      }).catch(() => {});
    },
    excerpt(text, n) {
      if (!text) return "";
      const oneLine = String(text).replace(/[\n\r]+/g, " / ");
      return oneLine.length > n ? oneLine.slice(0, n) + "…" : oneLine;
    },
    tsToDate(ts) {
      if (!ts) return "—";
      const d = new Date(ts * 1000);
      const p = v => (v < 10 ? "0" + v : v);
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + " " + p(d.getHours()) + ":" + p(d.getMinutes());
    },
    // 预加载筛选下拉选项：title(/travel/categories) + author(/travel/mng/authors)
    loadOptions() {
      var that = this;
      this.loadingOptions = true;
      Promise.all([
        axios.post(UseUrl + "/travel/categories", {}).then(function (r) {
          var data = (r && r.data) || {};
          that.titleOptions = Array.isArray(data.categories) ? data.categories.filter(Boolean) : [];
        }),
        axios.post(BASE + "/authors", {}).then(function (r) {
          var data = (r && r.data) || {};
          that.authorOptions = Array.isArray(data.authors) ? data.authors.filter(Boolean) : [];
        })
      ]).then(function () {
        that.loadingOptions = false;
      }).catch(function (err) {
        that.loadingOptions = false;
        var msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : (err && err.message) || "未知错误";
        that.$message && that.$message.error("筛选选项加载失败：" + msg);
        console.error("loadOptions failed", err);
      });
    }
  },
  mounted() {
    this.checkAuth();
    this.loadOptions();
    window.addEventListener("resize", this.onResize, false);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize, false);
  }
}
</script>

<style scoped>
.trvm-container {
  --sand: #f6f1e6;
  --card: #fffdf8;
  --deep: #0f4c52;
  --teal: #0f8b8d;
  --teal-soft: #58a7a6;
  --amber: #e08a3c;
  --ink: #1e2b2e;
  --ink-soft: #566a6b;
  --ink-faint: #93a3a2;
  --hairline: #dcd3c1;

  max-width: 1320px;
  margin: 0 auto;
  padding: 24px 20px 60px;
  font-family: "Noto Serif SC", "Songti SC", STSong, SimSun, serif;
  color: var(--ink);
}

.trvm-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--hairline);
  margin-bottom: 16px;
}

.trvm-title {
  margin: 0;
  font-size: 26px;
  letter-spacing: 5px;
  color: var(--deep);
}

.trvm-sub {
  color: var(--ink-faint);
  font-size: 12px;
  letter-spacing: 1px;
}

.trvm-user {
  color: var(--ink-faint);
  font-size: 13px;
  letter-spacing: 1px;
}

.trvm-actions {
  margin-left: auto;
  display: flex;
  gap: 10px;
  align-items: center;
}

.trvm-link {
  color: var(--teal);
  text-decoration: none;
  font-size: 13px;
  letter-spacing: 2px;
}

/* 未使用文件清理弹窗 */
.orphan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.orphan-sum {
  font-size: 13px;
  color: var(--ink-soft);
  letter-spacing: 1px;
}

.orphan-sum b {
  color: var(--teal);
  font-weight: 600;
}

.orphan-tip {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--ink-soft);
}

/* 未使用文件列表（普通表格，避免 el-table 在弹窗内布局失效） */
.orphan-table-wrap {
  max-height: 320px;
  overflow: auto;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  background: var(--card);
}

.orphan-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.orphan-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: 1px;
  background: var(--sand);
  border-bottom: 1px solid var(--hairline);
  white-space: nowrap;
}

.orphan-table tbody td {
  padding: 6px 10px;
  color: var(--ink-soft);
  border-bottom: 1px solid var(--hairline);
}

.orphan-table tbody tr:last-child td {
  border-bottom: none;
}

.orphan-table tbody tr {
  cursor: pointer;
}

.orphan-table tbody tr:hover td {
  background: rgba(15, 139, 141, 0.06);
}

.orphan-table tbody tr.is-sel td {
  background: rgba(15, 139, 141, 0.12);
}

.orphan-table .oc-check {
  width: 38px;
  text-align: center;
}

.orphan-table .oc-thumb {
  width: 58px;
  text-align: center;
}

.orphan-thumb {
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--hairline);
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  vertical-align: middle;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.orphan-thumb:hover {
  border-color: var(--teal);
  box-shadow: 0 0 0 2px rgba(15, 139, 141, 0.15);
}

.orphan-thumb.is-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.orphan-thumb.is-icon {
  color: var(--teal);
  background: rgba(15, 139, 141, 0.08);
}

.orphan-thumb.is-icon i {
  font-size: 18px;
}

.orphan-table .oc-size {
  width: 92px;
  white-space: nowrap;
}

.orphan-table .oc-time {
  width: 132px;
  white-space: nowrap;
}

.orphan-table .oc-name {
  max-width: 340px;
  color: var(--ink);
}

.oc-name-text {
  display: inline-block;
  max-width: 236px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.oc-kind {
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 16px;
  vertical-align: middle;
  color: var(--deep);
  background: rgba(15, 139, 141, 0.12);
  border-radius: 9px;
}

.oc-kind.k-track {
  color: #8a5a12;
  background: rgba(224, 138, 60, 0.18);
}

.oc-kind.k-video {
  color: #7a3d6a;
  background: rgba(150, 80, 140, 0.15);
}

/* 未使用文件·预览弹窗 */
.orphan-preview-dialog .op-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  max-height: 60vh;
  overflow: auto;
  padding: 10px;
  background: #0b1416;
  border-radius: 10px;
}

.orphan-preview-dialog .op-img {
  max-width: 100%;
  max-height: 58vh;
  object-fit: contain;
  display: block;
}

.orphan-preview-dialog .op-video {
  max-width: 100%;
  max-height: 58vh;
  display: block;
  background: #000;
}

.orphan-preview-dialog .op-void {
  padding: 28px 16px;
  text-align: center;
  color: #cfe3e0;
}

.orphan-preview-dialog .op-void i {
  font-size: 36px;
  color: var(--teal-soft);
}

.orphan-preview-dialog .op-void p {
  margin: 10px 0 0;
  font-size: 13px;
}

.orphan-preview-dialog .op-void-tip {
  color: #8ea7a5;
  font-size: 12px !important;
}

.orphan-preview-dialog .op-meta {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.orphan-preview-dialog .op-name {
  font-size: 13px;
  color: var(--ink);
  word-break: break-all;
}

.orphan-preview-dialog .op-info {
  font-size: 12px;
  color: var(--ink-faint);
}

.op-open-link {
  margin-right: 8px;
}

.orphan-empty {
  padding: 28px 0;
  text-align: center;
  font-size: 12px;
  color: var(--ink-faint);
}

/* 筛选区 */
.trvm-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 12px 14px;
  background: rgba(255, 253, 248, 0.9);
  border: 1px solid var(--hairline);
  border-radius: 10px;
  margin-bottom: 14px;
}

.f-w { width: 150px; }
.f-s { width: 110px; }
.f-id { width: 170px; }

.trvm-error {
  margin-bottom: 12px;
}

.trvm-loading-options {
  color: var(--ink-faint);
  font-size: 12px;
  letter-spacing: 1px;
  padding: 6px 0 10px;
}

/* 表格 */
.trvm-table-wrap {
  background: var(--card);
  border: 1px solid var(--hairline);
  border-radius: 10px;
  overflow-x: auto;
  box-shadow: 0 2px 10px rgba(15, 76, 82, 0.05);
}

.trvm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.trvm-table th {
  background: rgba(15, 139, 141, 0.1);
  color: var(--deep);
  font-weight: 600;
  letter-spacing: 2px;
  padding: 11px 10px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid var(--hairline);
}

.trvm-table td {
  padding: 9px 10px;
  border-bottom: 1px solid rgba(220, 211, 193, 0.6);
  vertical-align: middle;
}

.trvm-table tbody tr:hover {
  background: rgba(15, 139, 141, 0.05);
}

.c-title { min-width: 110px; font-weight: 600; letter-spacing: 1px; }
.c-subtitle { width: 110px; color: var(--ink-soft); }
.c-author { width: 100px; }
.c-content { max-width: 340px; color: var(--ink-soft); }
.c-media { width: 150px; white-space: nowrap; }
.c-time { width: 130px; color: var(--ink-faint); font-size: 12px; white-space: nowrap; }
.c-op { width: 140px; white-space: nowrap; }

.trvm-empty {
  text-align: center;
  color: var(--ink-faint);
  padding: 40px 0 !important;
  letter-spacing: 4px;
}

.trvm-pager {
  margin-top: 16px;
  text-align: right;
}

/* 附件标记 */
.media-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 8px;
  padding: 1px 8px;
  font-size: 12px;
  color: var(--deep);
  background: rgba(15, 139, 141, 0.12);
  border-radius: 12px;
}

.media-none { color: var(--ink-faint); }

/* 附件编辑区：轨迹 / 图片 / 视频 */
.trvm-block { width: 100%; }

.track-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  margin-bottom: 8px;
  background: rgba(15, 139, 141, 0.08);
  border: 1px solid var(--hairline);
  border-radius: 6px;
}

.track-name { font-weight: 600; }
.track-meta { color: var(--ink-faint); font-size: 12px; }
.upload-row { display: flex; align-items: center; gap: 12px; }
.upload-hint { color: var(--ink-faint); font-size: 12px; }

.media-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.media-cell {
  width: 118px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.media-thumb {
  position: relative;
  width: 100%;
  height: 88px;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.media-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

.media-desc >>> .el-input__inner {
  height: 26px;
  line-height: 26px;
  padding: 0 6px;
  font-size: 12px;
}

.media-add-box {
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--teal-soft);
  border-radius: 8px;
  color: var(--teal);
  font-size: 20px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
}

.media-add-box:hover { border-color: var(--teal); background: rgba(15, 139, 141, 0.08); }
.media-add-box.is-loading {
  flex-direction: column;
  gap: 4px;
  cursor: wait;
  border-style: solid;
  border-color: var(--teal);
  background: rgba(15, 139, 141, 0.08);
  font-size: 18px;
}

.media-add-pct {
  font-size: 11px;
  letter-spacing: 0.5px;
  color: var(--teal);
}

.media-del {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(30, 43, 46, 0.62);
  color: #fff;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
}

.media-del:hover { background: var(--amber); }

/* 图片上传占位：蒙层 + 进度 / 失败 */
.media-upmask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: rgba(30, 43, 46, 0.55);
  color: #fff;
  font-size: 16px;
}

.media-upmask.is-error { background: rgba(196, 88, 46, 0.62); }

.media-uppct { font-size: 11px; letter-spacing: 0.5px; }

.media-upname {
  height: 26px;
  line-height: 26px;
  font-size: 11px;
  color: var(--ink-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.video-list { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 8px; }

.video-item {
  position: relative;
  width: 176px;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  padding: 6px;
  background: #fff;
}

.video-thumb { width: 100%; height: 96px; object-fit: cover; display: block; background: #0b1819; border-radius: 4px; }

.video-name {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 视频上传占位 */
.video-upthumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #eafaf9;
  font-size: 18px;
}

.video-upthumb.is-error { background: rgba(196, 88, 46, 0.7); }

.video-uppct { font-size: 11px; letter-spacing: 0.5px; }

/* ============ 移动端适配 ============ */
@media (max-width: 768px) {
  .trvm-container {
    padding: 16px 12px 44px;
  }

  /* 页头：标题与用户名同行，操作区整行右对齐 */
  .trvm-header {
    flex-wrap: wrap;
    gap: 8px 10px;
  }

  .trvm-title {
    font-size: 20px;
    letter-spacing: 3px;
    flex: 1 1 auto;
  }

  .trvm-sub {
    display: none;
  }

  .trvm-user {
    font-size: 12px;
    margin-left: auto;
  }

  .trvm-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  /* 筛选区：输入两列排布，按钮整行等分 */
  .trvm-filters {
    padding: 10px;
    gap: 8px;
  }

  .f-w,
  .f-s,
  .f-id {
    width: calc(50% - 4px);
  }

  .trvm-filters > .el-button {
    flex: 1 1 0;
    margin-left: 0 !important;
  }

  /* 表格：转为卡片式堆叠，避免横向滚动 */
  .trvm-table-wrap {
    overflow-x: visible;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  .trvm-table thead {
    display: none;
  }

  .trvm-table,
  .trvm-table tbody,
  .trvm-table tr,
  .trvm-table td {
    display: block;
    width: 100%;
    max-width: none;
    box-sizing: border-box;
  }

  .trvm-table tr {
    background: var(--card);
    border: 1px solid var(--hairline);
    border-radius: 8px;
    margin-bottom: 10px;
    padding: 4px 0;
  }

  .trvm-table td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 8px 12px;
    border-bottom: 1px dashed rgba(220, 211, 193, 0.7);
    text-align: right;
  }

  .trvm-table td:last-child {
    border-bottom: none;
  }

  .trvm-table td::before {
    content: attr(data-label);
    flex: 0 0 auto;
    padding-top: 1px;
    font-size: 12px;
    letter-spacing: 2px;
    color: var(--ink-faint);
    text-align: left;
  }

  /* 长文本（内容）左右并排会挤压，改为上下堆叠左对齐 */
  .trvm-table td.c-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
  }

  .trvm-table td.c-op {
    justify-content: flex-end;
    gap: 8px;
  }

  .trvm-table td.c-op::before {
    margin-right: auto;
  }

  .trvm-table td.c-op .el-button + .el-button {
    margin-left: 0;
  }

  .trvm-table .trvm-empty {
    display: block;
    text-align: center;
    border-bottom: none;
  }

  .trvm-table .trvm-empty::before {
    content: none;
  }

  /* 分页居中 */
  .trvm-pager {
    text-align: center;
  }

  /* 弹窗内的附件编辑区：视频两列自适应 */
  .video-item {
    width: calc(50% - 5px);
  }

  .video-thumb {
    height: 78px;
  }

  /* 未使用文件：窄屏隐藏「修改时间」列，避免横向滚动 */
  .orphan-table th.oc-time,
  .orphan-table td.oc-time {
    display: none;
  }

  .oc-name-text {
    max-width: 140px;
  }
}
</style>

<style>
/* el-dialog 的容器不在 scoped 作用域内，用全局规则收紧移动端留白（custom-class 指定） */
@media (max-width: 768px) {
  .mng-form-dialog {
    margin: 6vh auto 30px !important;
  }

  .mng-form-dialog .el-dialog__header {
    padding: 14px 14px 8px;
  }

  .mng-form-dialog .el-dialog__body {
    padding: 12px 14px 16px;
  }

  .mng-form-dialog .el-dialog__footer {
    padding: 10px 14px 14px;
  }

  /* 分页项过多时允许换行，避免超出窄屏宽度 */
  .trvm-pager .el-pagination {
    white-space: normal;
  }
}
</style>
